// PNG / SVG / CSV export for the shared <Chart>.
//
// PNG: serialise SVG → Image → 2× canvas → toDataURL. We re-attach a small
// title + footer band so the exported asset stands alone in a slide deck.
// Footer always says "innodia.org" — never .eu (global rule).
//
// SVG: clone the live node, inline a minimum style block so the file
// renders standalone, ship as Blob.
//
// CSV: 3-column (group, count, pct) via lib/csv.ts.
import type { AggregationResult, ChartType } from "../../types/chart";
import { downloadBlob, rowsToCsv } from "../../lib/csv";

const FOOTER_TEXT = "Generated {date} · INPACT Dashboard · innodia.org";

function todayStamp(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}

function humanDate(): string {
  return new Date().toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
}

export function buildFilename(groupBy: string, ext: string): string {
  // group_by is "contact.Country_of_Residence_PL__c" → "Country_of_Residence_PL__c".
  const short = groupBy.split(".").slice(-1)[0] || "aggregate";
  return `inpact-aggregate-${short}-${todayStamp()}.${ext}`;
}

function findSvgFor(exportId: string): SVGSVGElement | null {
  return document.querySelector<SVGSVGElement>(`svg#${CSS.escape(exportId)}`);
}

function serializeSvgStandalone(svg: SVGSVGElement, title: string): string {
  // Clone so we can mutate dimensions / strip event listeners safely.
  const clone = svg.cloneNode(true) as SVGSVGElement;
  // Materialise computed colours that depend on CSS vars — without this the
  // exported SVG renders black on a fresh browser tab because --color-*
  // tokens aren't resolved outside the app's tokens.css.
  inlineCssVarColours(svg, clone);
  const vb = clone.getAttribute("viewBox") ?? "0 0 600 400";
  const [, , vbW, vbH] = vb.split(" ").map(Number);
  const padTop = 36;
  const padBottom = 24;
  const outW = vbW;
  const outH = vbH + padTop + padBottom;

  const wrapper = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  wrapper.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  wrapper.setAttribute("viewBox", `0 0 ${outW} ${outH}`);
  wrapper.setAttribute("width", String(outW));
  wrapper.setAttribute("height", String(outH));

  const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  bg.setAttribute("width", String(outW));
  bg.setAttribute("height", String(outH));
  bg.setAttribute("fill", "#ffffff");
  wrapper.appendChild(bg);

  const titleNode = document.createElementNS("http://www.w3.org/2000/svg", "text");
  titleNode.setAttribute("x", String(outW / 2));
  titleNode.setAttribute("y", "22");
  titleNode.setAttribute("text-anchor", "middle");
  titleNode.setAttribute("font-family", "Epilogue, Inter, sans-serif");
  titleNode.setAttribute("font-size", "16");
  titleNode.setAttribute("font-weight", "600");
  titleNode.setAttribute("fill", "#1E295E");
  titleNode.textContent = title;
  wrapper.appendChild(titleNode);

  const inner = document.createElementNS("http://www.w3.org/2000/svg", "g");
  inner.setAttribute("transform", `translate(0, ${padTop})`);
  // Append the cloned children, not the SVG itself, so the viewBox we set
  // on the wrapper is the authoritative one.
  while (clone.firstChild) inner.appendChild(clone.firstChild);
  wrapper.appendChild(inner);

  const footer = document.createElementNS("http://www.w3.org/2000/svg", "text");
  footer.setAttribute("x", String(outW / 2));
  footer.setAttribute("y", String(outH - 8));
  footer.setAttribute("text-anchor", "middle");
  footer.setAttribute("font-family", "Epilogue, Inter, sans-serif");
  footer.setAttribute("font-size", "10");
  footer.setAttribute("fill", "#777777");
  footer.textContent = FOOTER_TEXT.replace("{date}", humanDate());
  wrapper.appendChild(footer);

  return new XMLSerializer().serializeToString(wrapper);
}

function inlineCssVarColours(source: SVGSVGElement, clone: SVGSVGElement): void {
  // Walk both trees in parallel, copying resolved fill/stroke colours from
  // the live node onto the clone so CSS-var references survive the export.
  const liveNodes = source.querySelectorAll<SVGElement>("*");
  const cloneNodes = clone.querySelectorAll<SVGElement>("*");
  for (let i = 0; i < liveNodes.length && i < cloneNodes.length; i += 1) {
    const live = liveNodes[i];
    const cl = cloneNodes[i];
    const cs = window.getComputedStyle(live);
    const fill = cs.getPropertyValue("fill");
    const stroke = cs.getPropertyValue("stroke");
    if (fill && fill !== "none" && !cl.getAttribute("fill")?.startsWith("#")) {
      cl.setAttribute("fill", fill);
    }
    if (stroke && stroke !== "none" && !cl.getAttribute("stroke")?.startsWith("#")) {
      cl.setAttribute("stroke", stroke);
    }
  }
}

export interface ExportContext {
  exportId: string;
  title: string;
  groupBy: string;
  chartType: ChartType;
  rows: AggregationResult["rows"];
}

export function exportSvg(ctx: ExportContext): void {
  const svg = findSvgFor(ctx.exportId);
  if (!svg) throw new Error("The chart is not available for SVG export.");
  const xml = serializeSvgStandalone(svg, ctx.title);
  downloadBlob(xml, "image/svg+xml", buildFilename(ctx.groupBy, "svg"));
}

export function exportPng(ctx: ExportContext): Promise<void> {
  const svg = findSvgFor(ctx.exportId);
  if (!svg) return Promise.reject(new Error("The chart is not available for PNG export."));
  const xml = serializeSvgStandalone(svg, ctx.title);
  const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  return new Promise((resolve, reject) => {
    img.onload = () => {
      rasterise(img, ctx).then(resolve, reject).finally(() => URL.revokeObjectURL(url));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("The browser could not render this chart for PNG export."));
    };
    img.src = url;
  });
}

function rasterise(img: HTMLImageElement, ctx: ExportContext): Promise<void> {
  const w = img.width || 600;
  const h = img.height || 400;
  const scale = 2; // 2× pixel density for crisp slide-deck use.
  const canvas = document.createElement("canvas");
  canvas.width = w * scale;
  canvas.height = h * scale;
  const c = canvas.getContext("2d");
  if (!c) return Promise.reject(new Error("Canvas export is unavailable in this browser."));
  c.fillStyle = "#ffffff";
  c.fillRect(0, 0, canvas.width, canvas.height);
  c.scale(scale, scale);
  c.drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("The browser returned an empty PNG export."));
        return;
      }
      try {
        downloadImageBlob(blob, buildFilename(ctx.groupBy, "png"));
        resolve();
      } catch (error) {
        reject(error);
      }
    }, "image/png");
  });
}

function downloadImageBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  try {
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  } finally {
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export function exportCsv(ctx: ExportContext): void {
  const csv = rowsToCsv(
    ctx.rows.map((r) => ({
      group: r.label ?? r.group ?? "(unspecified)",
      count: r.count,
      pct: Number((r.pct * 100).toFixed(2)),
    })),
    [
      { key: "group", header: "Group" },
      { key: "count", header: "Count" },
      { key: "pct", header: "Percent" },
    ],
  );
  downloadBlob(csv, "text/csv", buildFilename(ctx.groupBy, "csv"));
}
